import { useLayoutEffect, useRef } from 'preact/hooks';
import { DEFAULT_RULES, kittySizeFor, partnersEnabled, type GameConfig } from '../engine';
import { Card } from './cards';

/**
 * A one-screen rules summary (RULES.md, condensed). With a game's `config`, the
 * configurable numbers are that game's; without one, the defaults.
 */
export function RulesSheet({ config, onClose }: { config?: GameConfig; onClose: () => void }) {
  // A ref keeps the keydown listener stable when the host re-renders with a fresh onClose.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const panel = useRef<HTMLDivElement>(null);
  // A layout effect, so Escape works as soon as the sheet is on screen (a plain
  // effect runs after paint, and a quick keypress could land before it).
  useLayoutEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>('[data-testid="close-rules"]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []);

  const rules = config?.rules ?? DEFAULT_RULES;
  const players = config?.playerCount;
  const kitty = players ? `${kittySizeFor(players, rules)} cards` : '4–6 cards, depending on the number of players';
  const partners = config ? partnersEnabled(config) : true;

  return (
    <div class="overlay" onClick={onClose}>
      <div
        class="panel rules"
        data-testid="rules"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rules-title"
        ref={panel}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="row between">
          <h2 id="rules-title">How to play</h2>
          <button class="small secondary" data-testid="close-rules" onClick={onClose}>
            Close
          </button>
        </div>

        <h3>Goal</h3>
        <p>
          First to <strong data-testid="rules-target">{rules.targetScore}</strong> points. If several players pass it on the same
          hand, the highest score wins; equal scores tie.
        </p>

        <h3>The deal</h3>
        <p>54 cards: the deck plus a high and a low joker. The kitty gets {kitty}; everything else is dealt out.</p>

        <h3>Bidding</h3>
        <ul>
          <li>
            Each bid names a suit, like "7 in clubs". The minimum is {rules.minBid}, and every bid must be higher than the last.
          </li>
          <li>Bidding goes around until everyone but the high bidder has passed. Once you pass, you're out.</li>
          <li>If nobody bids, the dealer is stuck with the minimum.</li>
          <li>13 means taking every point (±13). Shooting the moon outbids it: take every point for +26, or lose 26.</li>
        </ul>

        {partners ? (
          <>
            <h3>Calling your partner</h3>
            <ul>
              <li>The winner calls a card in their suit. Whoever holds it is their partner, and everyone is told.</li>
              <li>If the bidder holds it, or it's in the kitty, they play alone.</li>
              <li>Everyone who isn't the bidder or the partner defends together and shares their points.</li>
              <li>The called card leads the first trick: its holder must play it first, and play goes around from there.</li>
              <li>The bidder picks up the kitty and may pass any of its playable cards to the partner.</li>
            </ul>
          </>
        ) : (
          <>
            <h3>No partners</h3>
            <p>{players === 2 ? 'With two players' : 'In this game'}, the bidder plays alone and picks up the kitty.</p>
          </>
        )}

        <h3>Playable cards</h3>
        <p>
          Only the bid suit, the other Jack and 3 of the same colour, and both jokers are playable. Everyone keeps at most{' '}
          {rules.handLimit} of them; the rest are thrown away. Every trick is all trump, so the highest card wins.
        </p>
        <p class="small muted">Highest to lowest, with clubs bid:</p>
        <div class="rank-row" aria-label="Card ranking">
          {(['AC', 'KC', 'QC', 'JC', 'JS', 'BJ', 'LJ', 'TC', '9C', '3C', '3S', '2C'] as const).map((c) => (
            <Card key={c} card={c} width={34} />
          ))}
        </div>

        <h3>Points (13 a hand)</h3>
        <ul>
          <li>Ace, Jack, other Jack, each joker, and the 10: 1 point each, to whoever wins the trick.</li>
          <li>The 3 and the other 3: 3 points each, to whoever wins the trick.</li>
          <li>The 2: 1 point to whoever plays it. It can't be captured.</li>
        </ul>

        <h3>Scoring</h3>
        <ul>
          <li>Made it: the bidder and partner each score the points they took (bid 7, take 9: +9 each).</li>
          <li>Set: the bidder and partner each lose their bid, whatever they took.</li>
          <li>Defenders pool their points, and each defender scores the total.</li>
        </ul>

        <h3>Playing with a controller</h3>
        <ul data-testid="rules-controller">
          <li>D-pad or left stick: move between buttons and cards. LB / RB: previous or next card in your hand.</li>
          <li>A: choose. B: back (close this, finish the hand result, undo a bid pick). X: pass.</li>
          <li>Y: these rules. Menu: next hand, or send your bid.</li>
        </ul>
      </div>
    </div>
  );
}
